import React, { useContext, useEffect, lazy } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import AuthSimpleLayout from './AuthSimpleLayout';
import is from 'is_js';
import MainLayout from './MainLayout';
import SettingsPanel from 'components/settings-panel/SettingsPanel';
import Login from 'components/authentication/card/Login';
import ErrorLayout from './ErrorLayout';
import WizardAuth from 'components/authentication/wizard/WizardAuth';
import ProtectedRoute from 'routes/ProtectedRoute';

// Lazy-loaded components
const Landing = lazy(() => import('components/pages/landing/Landing'));
const Accordion = lazy(() => import('components/doc-components/Accordion'));
const Alerts = lazy(() => import('components/doc-components/Alerts'));
const Badges = lazy(() => import('components/doc-components/Badges'));
const Breadcrumbs = lazy(() => import('components/doc-components/Breadcrumb'));
const Buttons = lazy(() => import('components/doc-components/Buttons'));
const CalendarExample = lazy(() =>
  import('components/doc-components/CalendarExample')
);
const Cards = lazy(() => import('components/doc-components/Cards'));
const Dropdowns = lazy(() => import('components/doc-components/Dropdowns'));
const ListGroups = lazy(() => import('components/doc-components/ListGroups'));
const Modals = lazy(() => import('components/doc-components/Modals'));
const Offcanvas = lazy(() => import('components/doc-components/Offcanvas'));
const Pagination = lazy(() => import('components/doc-components/Pagination'));
const BasicProgressBar = lazy(() =>
  import('components/doc-components/ProgressBar')
);
const Spinners = lazy(() => import('components/doc-components/Spinners'));
const Toasts = lazy(() => import('components/doc-components/Toasts'));
const Avatar = lazy(() => import('components/doc-components/Avatar'));
const Image = lazy(() => import('components/doc-components/Image'));
const Tooltips = lazy(() => import('components/doc-components/Tooltips'));
const Popovers = lazy(() => import('components/doc-components/Popovers'));
const Figures = lazy(() => import('components/doc-components/Figures'));
const Hoverbox = lazy(() => import('components/doc-components/Hoverbox'));
const Tables = lazy(() => import('components/doc-components/Tables'));
const FormControl = lazy(() => import('components/doc-components/FormControl'));
const InputGroup = lazy(() => import('components/doc-components/InputGroup'));
const Select = lazy(() => import('components/doc-components/Select'));
const Checks = lazy(() => import('components/doc-components/Checks'));
const Range = lazy(() => import('components/doc-components/Range'));
const FormLayout = lazy(() => import('components/doc-components/FormLayout'));

import AppContext from 'context/Context';
const FloatingLabels = lazy(() =>
  import('components/doc-components/FloatingLabels')
);
const FormValidation = lazy(() =>
  import('components/doc-components/FormValidation')
);
const BootstrapCarousel = lazy(() =>
  import('components/doc-components/BootstrapCarousel')
);

import { toast, ToastContainer } from 'react-toastify';
import { CloseButton } from 'components/common/Toast';
import ConfirmMail from '../components/authentication/card/ConfirmMail';

const SlickCarousel = lazy(() =>
  import('components/doc-components/SlickCarousel')
);
const Navs = lazy(() => import('components/doc-components/Navs'));
const Navbars = lazy(() => import('components/doc-components/Navbars'));
const Tabs = lazy(() => import('components/doc-components/Tabs'));
const Collapse = lazy(() => import('components/doc-components/Collapse'));
const CountUp = lazy(() => import('components/doc-components/CountUp'));
const Embed = lazy(() => import('components/doc-components/Embed'));
const Background = lazy(() => import('components/doc-components/Backgrounds'));
const Search = lazy(() => import('components/doc-components/Search'));
const VerticalNavbar = lazy(() =>
  import('components/doc-components/VerticalNavbar')
);
const NavBarTop = lazy(() => import('components/doc-components/NavBarTop'));
const NavbarDoubleTop = lazy(() =>
  import('components/doc-components/NavbarDoubleTop')
);
const ComboNavbar = lazy(() => import('components/doc-components/ComboNavbar'));
const TypedText = lazy(() => import('components/doc-components/TypedText'));
const FileUploader = lazy(() =>
  import('components/doc-components/FileUploader')
);
const Borders = lazy(() => import('components/utilities/Borders'));
const Colors = lazy(() => import('components/utilities/Colors'));
const ColoredLinks = lazy(() => import('components/utilities/ColoredLinks'));
const Display = lazy(() => import('components/utilities/Display'));
const Visibility = lazy(() => import('components/utilities/Visibility'));
const StretchedLink = lazy(() => import('components/utilities/StretchedLink'));
const Float = lazy(() => import('components/utilities/Float'));
const Position = lazy(() => import('components/utilities/Position'));
const Spacing = lazy(() => import('components/utilities/Spacing'));
const Sizing = lazy(() => import('components/utilities/Sizing'));
const TextTruncation = lazy(() =>
  import('components/utilities/TextTruncation')
);
const Typography = lazy(() => import('components/utilities/Typography'));
const VerticalAlign = lazy(() => import('components/utilities/VerticalAlign'));
const Flex = lazy(() => import('components/utilities/Flex'));
const Grid = lazy(() => import('components/utilities/Grid'));
const WizardForms = lazy(() => import('components/doc-components/WizardForms'));
const GettingStarted = lazy(() =>
  import('components/documentation/GettingStarted')
);
const Configuration = lazy(() =>
  import('components/documentation/Configuration')
);
const DarkMode = lazy(() => import('components/documentation/DarkMode'));
const Plugins = lazy(() => import('components/documentation/Plugins'));
const Styling = lazy(() => import('components/documentation/Styling'));
const DesignFile = lazy(() => import('components/documentation/DesignFile'));
const Starter = lazy(() => import('components/pages/Starter'));
const AnimatedIcons = lazy(() =>
  import('components/doc-components/AnimatedIcons')
);
const DatePicker = lazy(() => import('components/doc-components/DatePicker'));
const FontAwesome = lazy(() => import('components/doc-components/FontAwesome'));
const Changelog = lazy(() =>
  import('components/documentation/change-log/ChangeLog')
);
const Analytics = lazy(() => import('components/dashboards/analytics'));
const Crm = lazy(() => import('components/dashboards/crm'));
const Saas = lazy(() => import('components/dashboards/saas'));
const Profile = lazy(() => import('components/pages/user/profile/Profile'));
const Associations = lazy(() =>
  import('components/pages/asscociations/Associations')
);
const Followers = lazy(() =>
  import('components/app/social/followers/Followers')
);
const Notifications = lazy(() =>
  import('components/app/social/notifications/Notifications')
);
const ActivityLog = lazy(() =>
  import('components/app/social/activity-log/ActivityLog')
);
const Settings = lazy(() => import('components/pages/user/settings/Settings'));
const Feed = lazy(() => import('components/app/social/feed/Feed'));
const Placeholder = lazy(() => import('components/doc-components/Placeholder'));
const Lightbox = lazy(() => import('components/doc-components/Lightbox'));
const AdvanceTableExamples = lazy(() =>
  import('components/doc-components/AdvanceTableExamples')
);
const ModalAuth = lazy(() =>
  import('components/authentication/modal/ModalAuth')
);
const Calendar = lazy(() => import('components/app/calendar/Calendar'));
const FaqAlt = lazy(() => import('components/pages/faq/faq-alt/FaqAlt'));
const FaqBasic = lazy(() => import('components/pages/faq/faq-basic/FaqBasic'));
const FaqAccordion = lazy(() =>
  import('components/pages/faq/faq-accordion/FaqAccordion')
);
const PrivacyPolicy = lazy(() =>
  import('components/pages/miscellaneous/privacy-policy/PrivacyPolicy')
);
const InvitePeople = lazy(() =>
  import('components/pages/miscellaneous/invite-people/InvitePeople')
);
const PricingDefault = lazy(() =>
  import('components/pages/pricing/pricing-default/PricingDefault')
);
const PricingAlt = lazy(() =>
  import('components/pages/pricing/pricing-alt/PricingAlt')
);
const Invoice = lazy(() => import('components/app/e-commerce/Invoice'));
const Billing = lazy(() => import('components/app/e-commerce/billing/Billing'));
const Checkout = lazy(() =>
  import('components/app/e-commerce/checkout/Checkout')
);
const ShoppingCart = lazy(() =>
  import('components/app/e-commerce/cart/ShoppingCart')
);
const CustomersDetails = lazy(() =>
  import('components/app/e-commerce/customers-details/CustomersDetails')
);
const OrderDetails = lazy(() =>
  import('components/app/e-commerce/orders/order-details/OrderDetails')
);
const Products = lazy(() =>
  import('components/app/e-commerce/product/Products')
);
const ProductDetails = lazy(() =>
  import('components/app/e-commerce/product/product-details/ProductDetails')
);
const Orders = lazy(() =>
  import('components/app/e-commerce/orders/order-list/Orders')
);
const Customers = lazy(() =>
  import('components/app/e-commerce/customers/Customers')
);
const Courses = lazy(() => import('components/app/e-learning/course/Courses'));
const CourseDetails = lazy(() =>
  import('components/app/e-learning/course/course-details')
);
const CreateCourse = lazy(() =>
  import('components/app/e-learning/course/create-a-course')
);
const TrainerProfile = lazy(() =>
  import('components/app/e-learning/trainer-profile')
);
const StudentOverview = lazy(() =>
  import('components/app/e-learning/student-overview')
);
const CreateEvent = lazy(() =>
  import('components/app/events/create-an-event/CreateEvent')
);
const EventList = lazy(() =>
  import('components/app/events/event-list/EventList')
);
const EventDetail = lazy(() =>
  import('components/app/events/event-detail/EventDetail')
);
const EmailDetail = lazy(() =>
  import('components/app/email/email-detail/EmailDetail')
);
const Compose = lazy(() => import('components/app/email/compose/Compose'));
const Inbox = lazy(() => import('components/app/email/inbox/Inbox'));
const Rating = lazy(() => import('components/doc-components/Rating'));
const AdvanceSelect = lazy(() =>
  import('components/doc-components/AdvanceSelect')
);
const Editor = lazy(() => import('components/doc-components/Editor'));
const Chat = lazy(() => import('components/app/chat/Chat'));
const Kanban = lazy(() => import('components/app/kanban/Kanban'));
const DraggableExample = lazy(() =>
  import('components/doc-components/DraggableExample')
);
const LineCharts = lazy(() =>
  import('components/doc-components/charts-example/echarts/line-charts')
);
const BarCharts = lazy(() =>
  import('components/doc-components/charts-example/echarts/bar-charts')
);
const CandlestickCharts = lazy(() =>
  import('components/doc-components/charts-example/echarts/candlestick-charts')
);
const GeoMaps = lazy(() =>
  import('components/doc-components/charts-example/echarts/geo-map')
);
const ScatterCharts = lazy(() =>
  import('components/doc-components/charts-example/echarts/scatter-charts')
);
const PieCharts = lazy(() =>
  import('components/doc-components/charts-example/echarts/pie-charts')
);
const RadarCharts = lazy(() =>
  import('components/doc-components/charts-example/echarts/radar-charts/Index')
);
const HeatmapCharts = lazy(() =>
  import('components/doc-components/charts-example/echarts/heatmap-chart')
);
const Chartjs = lazy(() =>
  import('components/doc-components/charts-example/chartjs')
);
const D3js = lazy(() => import('components/doc-components/charts-example/d3'));
const HowToUse = lazy(() =>
  import('components/doc-components/charts-example/echarts/HowToUse')
);
const GoogleMapExample = lazy(() =>
  import('components/doc-components/GoogleMapExample')
);
const LeafletMapExample = lazy(() =>
  import('components/doc-components/LeafletMapExample')
);
const CookieNoticeExample = lazy(() =>
  import('components/doc-components/CookieNoticeExample')
);
const Scrollbar = lazy(() => import('components/doc-components/Scrollbar'));
const Scrollspy = lazy(() => import('components/doc-components/Scrollspy'));
const ReactIcons = lazy(() => import('components/doc-components/ReactIcons'));
const ReactPlayerExample = lazy(() =>
  import('components/doc-components/ReactPlayerExample')
);
const EmojiMartExample = lazy(() =>
  import('components/doc-components/EmojiMart')
);
const TreeviewExample = lazy(() =>
  import('components/doc-components/TreeviewExample')
);
const Timeline = lazy(() => import('components/doc-components/Timeline'));
const Widgets = lazy(() => import('widgets/Widgets'));
const Ecommerce = lazy(() => import('components/dashboards/e-commerce'));
const Lms = lazy(() => import('components/dashboards/lms'));
const ProjectManagement = lazy(() =>
  import('components/dashboards/project-management')
);
const Error404 = lazy(() => import('components/errors/Error404'));
const Error500 = lazy(() => import('components/errors/Error500'));
const Logout = lazy(() => import('components/authentication/card/Logout'));
const SplitLogin = lazy(() => import('components/authentication/split/Login'));
const SplitLogout = lazy(() =>
  import('components/authentication/split/Logout')
);
const SplitRegistration = lazy(() =>
  import('components/authentication/split/Registration')
);
const SplitForgetPassword = lazy(() =>
  import('components/authentication/split/ForgetPassword')
);
const SplitPasswordReset = lazy(() =>
  import('components/authentication/split/PasswordReset')
);
const SplitConfirmMail = lazy(() =>
  import('components/authentication/split/ConfirmMail')
);
const SplitLockScreen = lazy(() =>
  import('components/authentication/split/LockScreen')
);
const Wizard = lazy(() => import('components/wizard/Wizard'));
const Faq = lazy(() => import('components/documentation/Faq'));
const SupportDesk = lazy(() => import('components/dashboards/support-desk'));
const TableView = lazy(() =>
  import('components/app/support-desk/tickets-layout/TableView')
);
const CardView = lazy(() =>
  import('components/app/support-desk/tickets-layout/CardView')
);
const Contacts = lazy(() =>
  import('components/app/support-desk/contacts/Contacts')
);
const ContactDetails = lazy(() =>
  import('components/app/support-desk/contact-details/ContactDetails')
);
const TicketsPreview = lazy(() =>
  import('components/app/support-desk/tickets-preview/TicketsPreview')
);
const QuickLinks = lazy(() =>
  import('components/app/support-desk/quick-links/QuickLinks')
);
const Reports = lazy(() =>
  import('components/app/support-desk/reports/Reports')
);
const Projects = lazy(() => import('components/dashboards/projects'));
const Project = lazy(() => import('components/project'));
const Team = lazy(() => import('components/pages/team/team'));
const Installation = lazy(() =>
  import('components/pages/installation/Installation')
);
const CreateProject = lazy(() =>
  import('../components/pages/createProject/CreateProject')
);

// Rest of the code remains the same

const Layout = () => {
  const HTMLClassList = document.getElementsByTagName('html')[0].classList;
  const {
    config: { navbarPosition }
  } = useContext(AppContext);

  useEffect(() => {
    if (is.windows()) {
      HTMLClassList.add('windows');
    }
    if (is.chrome()) {
      HTMLClassList.add('chrome');
    }
    if (is.firefox()) {
      HTMLClassList.add('firefox');
    }
    if (is.safari()) {
      HTMLClassList.add('safari');
    }
  }, [HTMLClassList]);

  useEffect(() => {
    if (navbarPosition === 'double-top') {
      HTMLClassList.add('double-top-nav-layout');
    }
    return () => HTMLClassList.remove('double-top-nav-layout');
  }, [navbarPosition]);

  return (
    <>
      <Routes>
        <Route path="/" element={<Landing />} />
        <Route element={<ErrorLayout />}>
          <Route path="errors/401" element={<Error404 />} />
          <Route path="errors/404" element={<Error404 />} />
          <Route path="errors/500" element={<Error500 />} />
        </Route>

        <Route path="welcome" element={<ConfirmMail />} />

        {/* Authentication routes */}
        <Route element={<AuthSimpleLayout />}>
          <Route
            path="authentication/simple/login"
            element={lazy(() =>
              import('components/authentication/simple/Login')
            )}
          />
          <Route
            path="authentication/simple/register"
            element={lazy(() =>
              import('components/authentication/simple/Registration')
            )}
          />
          <Route
            path="authentication/simple/logout"
            element={lazy(() =>
              import('components/authentication/simple/Logout')
            )}
          />
          <Route
            path="authentication/simple/forgot-password"
            element={lazy(() =>
              import('components/authentication/simple/ForgetPassword')
            )}
          />
          <Route
            path="authentication/simple/reset-password"
            element={lazy(() =>
              import('components/authentication/simple/PasswordReset')
            )}
          />
          <Route
            path="authentication/simple/confirm-mail"
            element={lazy(() =>
              import('components/authentication/simple/ConfirmMail')
            )}
          />
          <Route
            path="authentication/simple/lock-screen"
            element={lazy(() =>
              import('components/authentication/simple/LockScreen')
            )}
          />
        </Route>

        {/* Card routes */}
        <Route path="authentication/card/login" element={<Login />} />
        <Route
          path="authentication/card/register"
          element={lazy(() =>
            import('components/authentication/card/Registration')
          )}
        />
        <Route path="authentication/card/logout" element={<Logout />} />
        <Route
          path="authentication/card/forgot-password"
          element={lazy(() =>
            import('components/authentication/card/ForgetPassword')
          )}
        />
        <Route
          path="authentication/card/reset-password"
          element={lazy(() =>
            import('components/authentication/card/PasswordReset')
          )}
        />
        <Route
          path="authentication/card/confirm-mail"
          element={lazy(() =>
            import('components/authentication/card/ConfirmMail')
          )}
        />
        <Route
          path="authentication/card/lock-screen"
          element={lazy(() =>
            import('components/authentication/card/LockScreen')
          )}
        />

        {/*- ------------- Split ---------------------------  */}

        <Route path="authentication/split/login" element={<SplitLogin />} />
        <Route path="authentication/split/logout" element={<SplitLogout />} />
        <Route
          path="authentication/split/register"
          element={<SplitRegistration />}
        />
        <Route
          path="authentication/split/forgot-password"
          element={<SplitForgetPassword />}
        />
        <Route
          path="authentication/split/reset-password"
          element={<SplitPasswordReset />}
        />
        <Route
          path="authentication/split/confirm-mail"
          element={<SplitConfirmMail />}
        />
        <Route
          path="authentication/split/lock-screen"
          element={<SplitLockScreen />}
        />

        <Route element={<WizardAuth />}>
          <Route
            path="authentication/wizard"
            element={<Wizard validation={true} />}
          />
        </Route>

        {/* //--- MainLayout Starts  */}

        <Route element={<ProtectedRoute />}>
          <Route element={<MainLayout />}>
            {/*Dashboard*/}
            <Route path="dashboard" element={<Projects />} />
            {/* <Route path="dashboard" element={<h1>Dashboard</h1>} /> */}

            <Route
              path="dashboard/projects/create"
              element={<CreateProject />}
              exact
            />
            <Route
              path="dashboard/projects/:projectName"
              element={<Project />}
            />
            <Route
              path="dashboard/projects/:projectName/installation"
              element={<Installation />}
            />
            <Route path="dashboard/analytics" element={<Analytics />} />
            <Route path="dashboard/crm" element={<Crm />} />
            <Route path="dashboard/saas" element={<Saas />} />
            <Route path="dashboard/e-commerce" element={<Ecommerce />} />
            <Route path="dashboard/lms" element={<Lms />} />
            <Route
              path="dashboard/project-management"
              element={<ProjectManagement />}
            />
            <Route path="dashboard/support-desk" element={<SupportDesk />} />
            {/* E Commerce */}
            <Route
              path="e-commerce/orders/order-details"
              element={<OrderDetails />}
            />
            <Route path="e-commerce/orders/order-list" element={<Orders />} />
            <Route path="e-commerce/invoice" element={<Invoice />} />
            <Route path="e-commerce/billing" element={<Billing />} />
            <Route path="e-commerce/checkout" element={<Checkout />} />
            <Route path="e-commerce/shopping-cart" element={<ShoppingCart />} />
            <Route path="e-commerce/customers" element={<Customers />} />
            <Route
              path="e-commerce/customer-details"
              element={<CustomersDetails />}
            />

            <Route
              path="e-commerce/product/product-details"
              element={<ProductDetails />}
            />
            <Route
              path="e-commerce/product/product-details/:productId"
              element={<ProductDetails />}
            />

            <Route
              path="e-commerce/product/:productLayout"
              element={<Products />}
            />

            <Route path="e-commerce/invoice" element={<Invoice />} />

            {/* E Learning */}
            <Route
              path="e-learning/course/:courseLayout"
              element={<Courses />}
            />
            <Route
              path="e-learning/course/course-details"
              element={<CourseDetails />}
            />
            <Route
              path="e-learning/course/course-details/:courseId"
              element={<CourseDetails />}
            />
            <Route
              path="e-learning/course/create-a-course"
              element={<CreateCourse />}
            />
            <Route
              path="e-learning/trainer-profile"
              element={<TrainerProfile />}
            />
            <Route
              path="e-learning/student-overview"
              element={<StudentOverview />}
            />

            {/*icons*/}
            <Route path="icons/font-awesome" element={<FontAwesome />} />
            <Route path="icons/react-icons" element={<ReactIcons />} />

            {/* maps */}
            <Route path="maps/google-map" element={<GoogleMapExample />} />
            <Route path="maps/leaflet-map" element={<LeafletMapExample />} />

            {/*App*/}
            <Route path="app/calendar" element={<Calendar />} />
            <Route path="app/chat" element={<Chat />} />
            <Route path="app/kanban" element={<Kanban />} />
            <Route path="social/feed" element={<Feed />} />
            <Route path="social/activity-log" element={<ActivityLog />} />
            <Route path="social/notifications" element={<Notifications />} />
            <Route path="social/followers" element={<Followers />} />
            <Route path="events/event-detail" element={<EventDetail />} />
            <Route path="events/create-an-event" element={<CreateEvent />} />
            <Route path="events/event-list" element={<EventList />} />

            {/* Email */}
            <Route path="email/email-detail" element={<EmailDetail />} />
            <Route path="email/inbox" element={<Inbox />} />
            <Route path="email/compose" element={<Compose />} />

            {/* support desk */}
            <Route path="/support-desk/table-view" element={<TableView />} />
            <Route path="/support-desk/card-view" element={<CardView />} />
            <Route path="/support-desk/contacts" element={<Contacts />} />
            <Route
              path="/support-desk/contact-details"
              element={<ContactDetails />}
            />
            <Route
              path="/support-desk/tickets-preview"
              element={<TicketsPreview />}
            />
            <Route path="/support-desk/quick-links" element={<QuickLinks />} />
            <Route path="/support-desk/reports" element={<Reports />} />

            {/*Pages*/}
            <Route path="/account/team" element={<Team />} />
            <Route path="pages/starter" element={<Starter />} />
            <Route path="user/profile" element={<Profile />} />
            <Route path="user/settings" element={<Settings />} />
            <Route
              path="miscellaneous/associations"
              element={<Associations />}
            />
            <Route path="faq/faq-alt" element={<FaqAlt />} />
            <Route path="faq/faq-basic" element={<FaqBasic />} />
            <Route path="faq/faq-accordion" element={<FaqAccordion />} />
            <Route
              path="pricing/pricing-default"
              element={<PricingDefault />}
            />
            <Route path="pricing/pricing-alt" element={<PricingAlt />} />
            <Route
              path="miscellaneous/privacy-policy"
              element={<PrivacyPolicy />}
            />
            <Route
              path="miscellaneous/invite-people"
              element={<InvitePeople />}
            />
            {/* charts-example */}

            <Route path="charts/chartjs" element={<Chartjs />} />
            <Route path="charts/d3js" element={<D3js />} />
            <Route path="charts/echarts/line-charts" element={<LineCharts />} />
            <Route path="charts/echarts/bar-charts" element={<BarCharts />} />
            <Route
              path="charts/echarts/candlestick-charts"
              element={<CandlestickCharts />}
            />
            <Route path="charts/echarts/geo-map" element={<GeoMaps />} />
            <Route
              path="charts/echarts/scatter-charts"
              element={<ScatterCharts />}
            />
            <Route path="charts/echarts/pie-charts" element={<PieCharts />} />
            <Route
              path="charts/echarts/radar-charts"
              element={<RadarCharts />}
            />
            <Route
              path="charts/echarts/heatmap-charts"
              element={<HeatmapCharts />}
            />
            <Route path="charts/echarts/how-to-use" element={<HowToUse />} />

            {/*Components*/}
            <Route path="components/alerts" element={<Alerts />} />
            <Route path="components/accordion" element={<Accordion />} />
            <Route
              path="components/animated-icons"
              element={<AnimatedIcons />}
            />
            <Route path="components/badges" element={<Badges />} />
            <Route path="components/breadcrumb" element={<Breadcrumbs />} />
            <Route path="components/buttons" element={<Buttons />} />
            <Route path="components/calendar" element={<CalendarExample />} />
            <Route path="components/cards" element={<Cards />} />
            <Route path="components/dropdowns" element={<Dropdowns />} />
            <Route path="components/list-group" element={<ListGroups />} />
            <Route path="components/modals" element={<Modals />} />
            <Route path="components/offcanvas" element={<Offcanvas />} />
            <Route path="components/pagination" element={<Pagination />} />
            <Route
              path="components/progress-bar"
              element={<BasicProgressBar />}
            />
            <Route path="components/placeholder" element={<Placeholder />} />
            <Route path="components/spinners" element={<Spinners />} />
            <Route path="components/toasts" element={<Toasts />} />
            <Route path="components/pictures/avatar" element={<Avatar />} />
            <Route path="components/pictures/images" element={<Image />} />
            <Route path="components/pictures/figures" element={<Figures />} />
            <Route path="components/pictures/hoverbox" element={<Hoverbox />} />
            <Route path="components/pictures/lightbox" element={<Lightbox />} />
            <Route path="components/tooltips" element={<Tooltips />} />
            <Route path="components/popovers" element={<Popovers />} />
            <Route path="components/draggable" element={<DraggableExample />} />
            <Route path="components/scrollspy" element={<Scrollspy />} />
            <Route path="components/timeline" element={<Timeline />} />
            <Route path="components/treeview" element={<TreeviewExample />} />
            <Route
              path="components/carousel/bootstrap"
              element={<BootstrapCarousel />}
            />
            <Route
              path="components/carousel/slick"
              element={<SlickCarousel />}
            />
            <Route path="components/navs-and-tabs/navs" element={<Navs />} />
            <Route path="tables/basic-tables" element={<Tables />} />
            <Route
              path="tables/advance-tables"
              element={<AdvanceTableExamples />}
            />
            <Route path="forms/basic/form-control" element={<FormControl />} />
            <Route path="forms/basic/input-group" element={<InputGroup />} />
            <Route path="forms/basic/select" element={<Select />} />
            <Route path="forms/basic/checks" element={<Checks />} />
            <Route path="forms/basic/range" element={<Range />} />
            <Route path="forms/basic/layout" element={<FormLayout />} />
            <Route path="forms/advance/date-picker" element={<DatePicker />} />
            <Route path="forms/advance/editor" element={<Editor />} />
            <Route
              path="forms/advance/emoji-button"
              element={<EmojiMartExample />}
            />
            <Route
              path="forms/advance/advance-select"
              element={<AdvanceSelect />}
            />
            <Route
              path="forms/advance/file-uploader"
              element={<FileUploader />}
            />
            <Route path="forms/advance/rating" element={<Rating />} />
            <Route path="forms/floating-labels" element={<FloatingLabels />} />
            <Route path="forms/validation" element={<FormValidation />} />
            <Route path="forms/wizard" element={<WizardForms />} />
            <Route
              path="components/navs-and-tabs/navbar"
              element={<Navbars />}
            />
            <Route path="components/navs-and-tabs/tabs" element={<Tabs />} />
            <Route path="components/collapse" element={<Collapse />} />
            <Route
              path="components/cookie-notice"
              element={<CookieNoticeExample />}
            />
            <Route path="components/countup" element={<CountUp />} />
            <Route path="components/videos/embed" element={<Embed />} />
            <Route
              path="components/videos/react-player"
              element={<ReactPlayerExample />}
            />
            <Route path="components/background" element={<Background />} />
            <Route path="components/search" element={<Search />} />
            <Route path="components/typed-text" element={<TypedText />} />
            <Route
              path="components/navs-and-tabs/vertical-navbar"
              element={<VerticalNavbar />}
            />
            <Route
              path="components/navs-and-tabs/top-navbar"
              element={<NavBarTop />}
            />
            <Route
              path="components/navs-and-tabs/double-top-navbar"
              element={<NavbarDoubleTop />}
            />
            <Route
              path="components/navs-and-tabs/combo-navbar"
              element={<ComboNavbar />}
            />

            {/*Utilities*/}
            <Route path="utilities/borders" element={<Borders />} />
            <Route path="utilities/colors" element={<Colors />} />
            <Route path="utilities/colored-links" element={<ColoredLinks />} />
            <Route path="utilities/display" element={<Display />} />
            <Route path="utilities/visibility" element={<Visibility />} />
            <Route
              path="utilities/stretched-link"
              element={<StretchedLink />}
            />
            <Route
              path="utilities/stretched-link"
              element={<StretchedLink />}
            />
            <Route path="utilities/float" element={<Float />} />
            <Route path="utilities/position" element={<Position />} />
            <Route path="utilities/spacing" element={<Spacing />} />
            <Route path="utilities/sizing" element={<Sizing />} />
            <Route
              path="utilities/text-truncation"
              element={<TextTruncation />}
            />
            <Route path="utilities/typography" element={<Typography />} />
            <Route
              path="utilities/vertical-align"
              element={<VerticalAlign />}
            />
            <Route path="utilities/flex" element={<Flex />} />
            <Route path="utilities/grid" element={<Grid />} />
            <Route path="utilities/scroll-bar" element={<Scrollbar />} />

            <Route path="widgets" element={<Widgets />} />

            {/*Documentation*/}
            <Route
              path="documentation/getting-started"
              element={<GettingStarted />}
            />
            <Route
              path="documentation/configuration"
              element={<Configuration />}
            />
            <Route path="documentation/styling" element={<Styling />} />
            <Route path="documentation/dark-mode" element={<DarkMode />} />
            <Route path="documentation/plugin" element={<Plugins />} />
            <Route path="documentation/faq" element={<Faq />} />
            <Route path="documentation/design-file" element={<DesignFile />} />
            <Route path="changelog" element={<Changelog />} />
            <Route path="authentication-modal" element={<ModalAuth />} />
          </Route>
        </Route>

        {/* //--- MainLayout end  */}

        {/* <Navigate to="/errors/404" /> */}
        <Route path="*" element={<Navigate to="/errors/404" replace />} />
      </Routes>
      <SettingsPanel />
      <ToastContainer
        closeButton={CloseButton}
        icon={false}
        position={toast.POSITION.BOTTOM_LEFT}
      />
    </>
  );
};

export default Layout;
